import { supabase } from '../supabaseClient';

export interface AnalyticsEmbedConfig {
    workspaceId: string;
    reportId: string;
    embedUrl: string;
    accessToken: string;
    tokenType: 'Embed' | 'Aad';
    permissions: 'Read' | 'ReadWrite';
    datasetId?: string;
    expiresAt?: string;
    status?: string;
}

export interface AnalyticsServiceStatus {
    isConfigured: boolean;
    sqlSchemaStatus: string;
    ppcValidValue: string;
    ppcGrossValue: string;
}

/**
 * Retrieves local/environment configuration status for local Analytics.
 */
export function getAnalyticsConfigStatus(): AnalyticsServiceStatus {
    return {
        isConfigured: true,
        sqlSchemaStatus: 'CAPA SQL DESPLEGADA (Esquema bi con 7 Vistas & Rol bi_reader)',
        ppcValidValue: '85.61% (Last Planner System Standard)',
        ppcGrossValue: '45.20% (Modelo Semántico DAX)'
    };
}

const getMsalHeaders = () => {
    const msalToken = typeof window !== 'undefined' ? localStorage.getItem('klyd_msal_access_token') : null;
    const headers: Record<string, string> = {};
    if (msalToken) {
        headers['X-MSAL-Token'] = msalToken;
    }
    return headers;
};

/**
 * Generates or fetches an Embed Token from the Supabase Edge Function.
 */
export async function fetchAnalyticsEmbedToken(
    reportId?: string,
    mode: 'view' | 'edit' = 'view',
    context: 'consumption' | 'designer' = 'consumption',
    actionOverride?: 'view' | 'edit' | 'create',
    datasetId?: string
): Promise<Partial<AnalyticsEmbedConfig>> {
    if (!reportId && actionOverride !== 'create') {
        return {
            reportId: '',
            accessToken: '',
            embedUrl: '',
            tokenType: 'Embed',
            permissions: mode === 'edit' ? 'ReadWrite' : 'Read'
        };
    }

    // Call the Supabase Edge Function with action parameter
    const { data, error } = await supabase.functions.invoke('generate-embed-token', {
        headers: getMsalHeaders(),
        body: {
            action: actionOverride || (mode === 'edit' ? 'edit' : 'view'),
            reportId,
            datasetId
        }
    });

    if (error) {
        throw error;
    }

    if (data && data.error) {
        const parsedErr = new Error(data.error.message || 'Error analítico del servidor');
        (parsedErr as any).code = data.error.code || 'SERVER_ERROR';
        (parsedErr as any).requestId = data.error.requestId;
        throw parsedErr;
    }

    return {
        reportId: data.reportId || reportId || '',
        embedUrl: data.embedUrl || '',
        accessToken: data.embedToken || '',
        tokenType: 'Embed',
        permissions: data.mode === 'edit' ? 'ReadWrite' : 'Read',
        status: data.status || 'OK',
        expiresAt: data.expiresAt,
        isMocked: data.isMocked,
        name: data.name,
        metadata: data.metadata
    } as any;
}

/**
 * Lista todos los reportes del catálogo a los que tiene acceso el usuario actual.
 */
export async function fetchAnalyticsReportsCatalog(): Promise<{ reports: any[], projects: any[], datasets?: any[], datasetError?: any }> {
    const { data, error } = await supabase.functions.invoke('generate-embed-token', {
        headers: getMsalHeaders(),
        body: { action: 'list' }
    });
    if (error) throw error;
    if (data && data.error) throw new Error(data.error.message);
    return {
        reports: data.reports || [],
        projects: data.projects || [],
        datasets: data.datasets || [],
        datasetError: data.datasetError || null
    };
}

/**
 * Genera un token de creación para abrir el lienzo vacío del reporte.
 */
export async function fetchAnalyticsEmbedTokenForCreate(datasetId?: string): Promise<Partial<AnalyticsEmbedConfig>> {
    const { data, error } = await supabase.functions.invoke('generate-embed-token', {
        headers: getMsalHeaders(),
        body: { action: 'create', datasetId }
    });
    if (error) throw error;
    if (data && data.error) throw new Error(data.error.message);
    return {
        embedUrl: data.embedUrl,
        accessToken: data.embedToken,
        tokenType: 'Embed',
        expiresAt: data.expiresAt,
        datasetId: data.datasetId
    };
}

/**
 * Registra un nuevo reporte en el catálogo de K-LyDApp.
 */
export async function registerAnalyticsReport(
    analyticsReportId: string,
    name: string,
    scope: 'obra' | 'multiobra' | 'corporativo',
    projectIds: string[],
    metadata?: any
): Promise<any> {
    const { data, error } = await supabase.functions.invoke('generate-embed-token', {
        headers: getMsalHeaders(),
        body: {
            action: 'register',
            reportId: analyticsReportId,
            name,
            scope,
            projectIds,
            metadata
        }
    });
    if (error) throw error;
    if (data && data.error) {
        const err = new Error(data.error.message);
        (err as any).code = data.error.code;
        throw err;
    }
    return data;
}

/**
 * Elimina un reporte del catálogo.
 */
export async function deleteAnalyticsReport(reportId: string): Promise<any> {
    const { data, error } = await supabase.functions.invoke('generate-embed-token', {
        headers: getMsalHeaders(),
        body: {
            action: 'delete',
            reportId
        }
    });
    if (error) throw error;
    if (data && data.error) {
        throw new Error(data.error.message);
    }
    return data;
}

/**
 * Ejecuta un diagnóstico de conexión analítica.
 */
export async function runAnalyticsDiagnostic(): Promise<any> {
    const { data, error } = await supabase.functions.invoke('generate-embed-token', {
        headers: getMsalHeaders(),
        body: { action: 'diagnose' }
    });
    if (error) throw error;
    if (data && data.error) {
        throw new Error(data.error.message);
    }
    return data;
}
